import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import {
  AttachmentFileSource,
  canPreviewInBrowser,
  downloadAttachment,
  fetchRemoteFileBlob,
} from '../utility/openRemoteFile';

type AttachmentPreviewModalProps = {
  opened: boolean;
  onClose: () => void;
  source: AttachmentFileSource | null;
  accessToken: string | null;
};

export function AttachmentPreviewModal({
  opened,
  onClose,
  source,
  accessToken,
}: AttachmentPreviewModalProps) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
  };

  useEffect(() => {
    if (!opened || !source) {
      revokeBlobUrl();
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;

    const loadPreview = async () => {
      revokeBlobUrl();
      setLoading(true);
      setError(null);

      try {
        let objectUrl: string;
        if (source.localFile) {
          objectUrl = URL.createObjectURL(source.localFile);
        } else if (source.filePath) {
          const blob = await fetchRemoteFileBlob(source.filePath, accessToken);
          objectUrl = URL.createObjectURL(blob);
        } else {
          throw new Error('No file source');
        }

        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        blobUrlRef.current = objectUrl;
        setBlobUrl(objectUrl);
      } catch {
        if (active) {
          setError(t('common.attachments.previewFailed'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      active = false;
    };
  }, [opened, source, accessToken, t]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    revokeBlobUrl();
    setError(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!source) return;
    setDownloading(true);
    try {
      await downloadAttachment(source, accessToken);
    } catch {
      setError(t('common.attachments.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const previewable = source ? canPreviewInBrowser(source.fileName) : false;
  const isPdf = source ? /\.pdf$/i.test(source.fileName) : false;
  const isImage = previewable && !isPdf;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={source?.fileName ?? t('common.attachments.preview')}
      size="xl"
      centered
      styles={{
        body: { padding: 0 },
        header: { marginBottom: 0 },
      }}
    >
      <Stack spacing={0}>
        <Group position="right" px="md" py="xs" sx={{ borderBottom: '1px solid #eee' }}>
          <Button
            size="xs"
            variant="light"
            leftIcon={<IconDownload size={14} />}
            loading={downloading}
            onClick={() => void handleDownload()}
          >
            {t('common.attachments.download')}
          </Button>
        </Group>

        <div style={{ minHeight: 420, maxHeight: '70vh', overflow: 'auto', padding: '1rem' }}>
          {loading && (
            <Group position="center" py="xl">
              <Loader size="md" />
            </Group>
          )}

          {!loading && error && (
            <Stack align="center" spacing="md" py="xl">
              <Text color="red" size="sm">
                {error}
              </Text>
              <Button size="sm" variant="light" onClick={() => void handleDownload()}>
                {t('common.attachments.download')}
              </Button>
            </Stack>
          )}

          {!loading && !error && blobUrl && previewable && isPdf && (
            <iframe
              src={blobUrl}
              title={source?.fileName}
              style={{ width: '100%', height: '65vh', border: 'none' }}
            />
          )}

          {!loading && !error && blobUrl && isImage && (
            <img
              src={blobUrl}
              alt={source?.fileName}
              style={{ maxWidth: '100%', maxHeight: '65vh', display: 'block', margin: '0 auto' }}
            />
          )}

          {!loading && !error && blobUrl && !previewable && (
            <Stack align="center" spacing="md" py="xl">
              <Text size="sm" color="dimmed" align="center">
                {t('common.attachments.previewUnsupported')}
              </Text>
              <Button size="sm" variant="light" onClick={() => void handleDownload()}>
                {t('common.attachments.download')}
              </Button>
            </Stack>
          )}
        </div>
      </Stack>
    </Modal>
  );
}

export function useAttachmentPreview(accessToken: string | null) {
  const [previewSource, setPreviewSource] = useState<AttachmentFileSource | null>(null);

  return {
    openPreview: (source: AttachmentFileSource) => setPreviewSource(source),
    closePreview: () => setPreviewSource(null),
    previewModal: (
      <AttachmentPreviewModal
        opened={previewSource !== null}
        onClose={() => setPreviewSource(null)}
        source={previewSource}
        accessToken={accessToken}
      />
    ),
  };
}
